import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetStockTransfers,
  GetStockTransferById,
  CreateStockTransfer,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

export const stockTransferQueryKeys = {
  stockTransfers: (params?: Parameters<typeof GetStockTransfers>[0]) =>
    ["stockTransfers", params] as const,
  stockTransferById: (params?: Parameters<typeof GetStockTransferById>[0]) =>
    ["stockTransferById", params] as const,
};

export const invalidateStockTransferQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["stockTransfers"] });
  queryClient.invalidateQueries({ queryKey: ["stockTransferById"] });
};

export const useStockTransfersQuery = (
  params: Parameters<typeof GetStockTransfers>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: stockTransferQueryKeys.stockTransfers(params),
    queryFn: async () => unwrapApiResponse(await GetStockTransfers(params)),
  });
};

export const useStockTransfersRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetStockTransfers>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: stockTransferQueryKeys.stockTransfers(params),
        queryFn: async () => unwrapApiResponse(await GetStockTransfers(params)),
      }),
  });
};

export const useStockTransferByIdQuery = (
  params: Parameters<typeof GetStockTransferById>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: stockTransferQueryKeys.stockTransferById(params),
    queryFn: async () => unwrapApiResponse(await GetStockTransferById(params)),
  });
};

export const useStockTransferByIdRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetStockTransferById>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: stockTransferQueryKeys.stockTransferById(params),
        queryFn: async () =>
          unwrapApiResponse(await GetStockTransferById(params)),
      }),
  });
};

export const useCreateStockTransferMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateStockTransfer>[0]) =>
      CreateStockTransfer(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateStockTransferQueries(queryClient);
      }
    },
  });
};

export const useStockTransferService = (config?: {
  fetchStockTransfersParams?: Parameters<typeof GetStockTransfers>[0];
  fetchStockTransfersEnabled?: boolean;
  fetchStockTransferByIdParams?: Parameters<typeof GetStockTransferById>[0];
  fetchStockTransferByIdEnabled?: boolean;
}) => {
  const stockTransfersQuery = useStockTransfersQuery(
    config?.fetchStockTransfersParams,
    {
      enabled:
        config?.fetchStockTransfersEnabled !== false &&
        config?.fetchStockTransfersParams !== undefined &&
        config?.fetchStockTransfersParams !== null,
    },
  );

  const stockTransferByIdQuery = useStockTransferByIdQuery(
    config?.fetchStockTransferByIdParams,
    {
      enabled:
        config?.fetchStockTransferByIdEnabled !== false &&
        config?.fetchStockTransferByIdParams !== undefined &&
        config?.fetchStockTransferByIdParams !== null,
    },
  );

  const createStockTransferMutation = useCreateStockTransferMutation();

  return {
    stockTransfersQuery,
    stockTransferByIdQuery,
    createStockTransferMutation,
  };
};
